import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import CepInput from '../../../components/shared/CepInput';
import { maskPhone, maskCPFOrCNPJ } from '../../../utils/masks';
import { ArrowLeft, Save } from 'lucide-react';

const TIPOS = ['Motoboy', 'Transportadora', 'Terceirizado'];

export default function TransportadoraForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'nova';
  const [form, setForm] = useState({ tipo_pessoa: 'F', nome: '', cpf: '', cnpj: '', nome_fantasia: '', razao_social: '', inscricao_estadual: '', site: '', telefone: '', whatsapp: '', cep: '', logradouro: '', numero: '', bairro: '', cidade: '', uf: '', tipo: 'Motoboy', is_ativo: true });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isNew) api.get(`/transportadoras/${id}`).then(r => setForm({ ...form, ...r.data })).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleCep = (addr) => addr && setForm(f => ({ ...f, logradouro: addr.logradouro, bairro: addr.bairro, cidade: addr.cidade, uf: addr.uf }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post('/transportadoras', form);
      else await api.put(`/transportadoras/${id}`, form);
      navigate('/admin/transportadoras');
    } catch (err) { setError(err.userMessage || 'Erro ao salvar'); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>;
  const inputCls = "w-full rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid focus:border-brand-primary bg-white";

  return (
    <div data-testid="transportadora-form">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-brand-bg"><ArrowLeft className="w-5 h-5 text-brand-text-muted" /></button>
        <h1 className="dashboard-heading text-xl">{isNew ? 'Novo Entregador / Transportadora' : 'Editar'}</h1>
      </div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}
      <div className="card-dashboard max-w-2xl space-y-4">
        <div>
          <label className="block text-xs text-brand-text-muted mb-2 font-body">Tipo de Pessoa</label>
          <div className="flex gap-4">
            {[{ v: 'F', l: 'Pessoa Física' }, { v: 'J', l: 'Pessoa Jurídica' }].map(o => (
              <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={form.tipo_pessoa === o.v} onChange={() => set('tipo_pessoa', o.v)} />
                <span className="text-sm font-body text-brand-text">{o.l}</span>
              </label>
            ))}
          </div>
        </div>
        {form.tipo_pessoa === 'F' ? (
          <>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Nome Completo *</label><input className={inputCls} value={form.nome} onChange={e => set('nome', e.target.value)} data-testid="field-nome" /></div>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">CPF</label><input className={inputCls} value={form.cpf} onChange={e => set('cpf', maskCPFOrCNPJ(e.target.value))} /></div>
          </>
        ) : (
          <>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">CNPJ</label><input className={inputCls} value={form.cnpj} onChange={e => set('cnpj', maskCPFOrCNPJ(e.target.value))} /></div>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Nome Fantasia *</label><input className={inputCls} value={form.nome_fantasia} onChange={e => set('nome_fantasia', e.target.value)} /></div>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Razão Social</label><input className={inputCls} value={form.razao_social} onChange={e => set('razao_social', e.target.value)} /></div>
          </>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Tipo *</label><select className={inputCls} value={form.tipo} onChange={e => set('tipo', e.target.value)}>{TIPOS.map(t => <option key={t}>{t}</option>)}</select></div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">WhatsApp</label><input className={inputCls} value={form.whatsapp} onChange={e => set('whatsapp', maskPhone(e.target.value))} /></div>
        </div>
        <div><label className="block text-xs text-brand-text-muted mb-1 font-body">CEP</label><CepInput value={form.cep} onChange={v => set('cep', v)} onAddressFound={handleCep} className={inputCls} /></div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><label className="block text-xs text-brand-text-muted mb-1 font-body">Logradouro</label><input className={inputCls} value={form.logradouro} onChange={e => set('logradouro', e.target.value)} /></div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Número</label><input className={inputCls} value={form.numero} onChange={e => set('numero', e.target.value)} /></div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_ativo} onChange={e => set('is_ativo', e.target.checked)} /><span className="text-sm font-body text-brand-text">Ativo</span></label>
        <div className="flex gap-3 pt-2">
          <button onClick={() => navigate(-1)} className="px-5 py-2.5 rounded-lg border border-brand-border font-body text-sm">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm flex items-center gap-2" data-testid="save-transp-btn"><Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  );
}
